/**
 * Przepisanie stylów ASS pod preferencje widza.
 *
 * libass-wasm **nie ma API do skalowania tekstu**: instancja przyjmuje treść
 * napisów i rysuje ją dokładnie tak, jak każe plik. Powiększenie przez CSS
 * (`transform: scale`) rozjechałoby pozycjonowanie i znaki umieszczane komendami
 * `\pos`, bo skalowałoby całe płótno razem z układem — napisy wyszłyby za obraz.
 *
 * Zostaje jedyna uczciwa droga: **zmienić plik przed podaniem go rendererowi**.
 * Blok `[V4+ Styles]` niesie rozmiar, krój, grubość obrysu, cień i marginesy dla
 * każdego stylu osobno, a `[Script Info]` decyduje, czy obrys skaluje się razem
 * z rozdzielczością. Modyfikujemy je liczbowo, zachowując resztę pliku bez zmian.
 *
 * Czego świadomie NIE robimy: nie ruszamy kolorów ani komend inline (`{\c&H…}`).
 * Fansub używa koloru do rozróżniania mówiących i do napisów na ekranie, więc
 * nadpisanie go zniszczyłoby informację, a nie tylko wygląd.
 */

export interface SubtitleStyle {
  /** Mnożnik rozmiaru pisma. 1 to rozmiar z pliku. */
  scale: number;
  /** Mnożnik grubości obrysu. Obrys jest tym, co czyni tekst czytelnym na jasnej scenie. */
  outline: number;
  /**
   * Krój wymuszony dla wszystkich stylów albo `null` = jak w pliku.
   *
   * Wymuszenie ma jeden konkretny powód: kroje z ASS-a rzadko istnieją
   * u odbiorcy, a wtedy libass sięga po zapasowy i wynik bywa gorszy niż jawny
   * wybór czytelnego kroju.
   */
  fontFamily: string | null;
  /** Przesunięcie napisów w górę, w jednostkach ASS (marginesy liczą się w pikselach `PlayResY`). */
  marginBottom: number;
}

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  scale: 1,
  outline: 1,
  fontFamily: null,
  marginBottom: 0,
};

/** Granice pochodzą z użycia, nie z estetyki: poniżej 0,6 tekst jest nieczytelny, powyżej 2,5 zasłania obraz. */
export const SCALE_RANGE = { min: 0.6, max: 2.5, step: 0.05 } as const;
export const OUTLINE_RANGE = { min: 0, max: 4, step: 0.25 } as const;
export const MARGIN_RANGE = { min: 0, max: 240, step: 10 } as const;

/** Kroje do wyboru. Zapasowy libassa jest Noto Sans, więc „auto" nie znaczy „bez kroju". */
export const FONT_CHOICES: ReadonlyArray<{ value: string | null; label: string }> = [
  { value: null, label: 'Jak w napisach' },
  { value: 'Noto Sans', label: 'Noto Sans (bezszeryfowy)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Verdana', label: 'Verdana (szeroki)' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: 'Georgia', label: 'Georgia (szeryfowy)' },
];

export function isDefaultStyle(style: SubtitleStyle): boolean {
  return (
    style.scale === DEFAULT_SUBTITLE_STYLE.scale &&
    style.outline === DEFAULT_SUBTITLE_STYLE.outline &&
    style.fontFamily === DEFAULT_SUBTITLE_STYLE.fontFamily &&
    style.marginBottom === DEFAULT_SUBTITLE_STYLE.marginBottom
  );
}

/**
 * Zwraca treść ASS z przepisanym blokiem stylów.
 *
 * Wejście nierozpoznane oddajemy **bez zmian**. Plik bez bloku `[V4+ Styles]` albo
 * z nietypowym nagłówkiem `Format:` nie jest błędem widza — lepiej pokazać napisy
 * w oryginalnym rozmiarze niż nie pokazać ich wcale.
 */
export function applyAssStyle(source: string, style: SubtitleStyle): string {
  if (isDefaultStyle(style)) return source;

  const lines = source.split(/\r?\n/);
  /**
   * Indeksy kolumn czytamy z nagłówka `Format:`, nie z pozycji utrwalonej w kodzie.
   *
   * Kolejność kolumn w ASS jest deklarowana per plik i realnie się różni —
   * `Fontsize` bywa trzeci, bywa czwarty. Twarde indeksy dawałyby cicho zepsute
   * napisy: podmieniony byłby wtedy kolor albo pogrubienie.
   */
  let columns: string[] | null = null;
  let inStyles = false;
  let touchedScriptInfo = false;

  const out = lines.map((line) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('[')) {
      inStyles = /^\[v4\+? styles\]$/i.test(trimmed);
      return line;
    }

    /**
     * `ScaledBorderAndShadow: yes` w `[Script Info]`.
     *
     * Bez tego obrys i cień są liczone w pikselach ekranu, nie w jednostkach
     * `PlayRes`, więc pogrubiony obrys na telewizorze 4K wyglądałby jak włos.
     * Ustawiamy to tylko wtedy, gdy widz obrys ruszał.
     */
    if (style.outline !== 1 && /^ScaledBorderAndShadow\s*:/i.test(trimmed)) {
      touchedScriptInfo = true;
      return 'ScaledBorderAndShadow: yes';
    }

    if (!inStyles) return line;

    if (/^Format\s*:/i.test(trimmed)) {
      columns = trimmed
        .slice(trimmed.indexOf(':') + 1)
        .split(',')
        .map((name) => name.trim().toLowerCase());
      return line;
    }

    if (!/^Style\s*:/i.test(trimmed) || columns === null) return line;

    const values = trimmed
      .slice(trimmed.indexOf(':') + 1)
      .split(',')
      .map((value) => value.trim());

    // Nazwa stylu może zawierać przecinek tylko w plikach uszkodzonych; przy
    // niezgodnej liczbie kolumn nie zgadujemy i zostawiamy wiersz w spokoju.
    if (values.length < columns.length) return line;

    const patched = [...values];
    patchNumeric(patched, columns, 'fontsize', (n) => n * style.scale, 0);
    patchNumeric(patched, columns, 'outline', (n) => n * style.outline, 2);
    // Cień idzie razem z obrysem: cień bez obrysu na jasnej scenie znika,
    // a osobny suwak na coś, czego nikt nie reguluje oddzielnie, byłby ozdobą.
    patchNumeric(patched, columns, 'shadow', (n) => n * style.outline, 2);
    patchNumeric(patched, columns, 'marginv', (n) => n + style.marginBottom, 0);

    if (style.fontFamily !== null) {
      const index = columns.indexOf('fontname');
      if (index >= 0) patched[index] = style.fontFamily;
    }

    // Odtwarzamy wiersz w postaci kanonicznej („Style: " plus wartości po
    // przecinkach). Wartości i tak przeszły przez `trim`, więc zachowywanie
    // oryginalnych odstępów byłoby udawaniem, że nic nie zmieniliśmy.
    return `Style: ${patched.join(',')}`;
  });

  /**
   * Gdy pliku nie ma czego skalować w `[Script Info]`, dopisujemy wprost.
   *
   * Część fansubów pomija tę linię, a jej brak znaczy „nie skaluj" — czyli
   * dokładnie to, czego przy zmienionym obrysie nie chcemy.
   */
  if (style.outline !== 1 && !touchedScriptInfo) {
    const index = out.findIndex((line) => /^\[script info\]$/i.test(line.trim()));
    if (index >= 0) out.splice(index + 1, 0, 'ScaledBorderAndShadow: yes');
  }

  return out.join('\n');
}

function patchNumeric(
  values: string[],
  columns: string[],
  column: string,
  transform: (value: number) => number,
  digits: number,
): void {
  const index = columns.indexOf(column);
  if (index < 0) return;
  const current = Number.parseFloat(values[index] ?? '');
  if (!Number.isFinite(current)) return;
  const next = transform(current);
  // Zaokrąglenie do zera miejsc dla rozmiaru i marginesu, dwóch dla obrysu:
  // libass przyjmuje ułamki, ale plik czyta potem człowiek przy diagnostyce.
  values[index] = digits === 0 ? String(Math.round(next)) : next.toFixed(digits);
}
